import {useEffect, useState} from "react";

import {MapEmbed} from "@/components/ui/map-embed";
import {Card, CardContent} from "@/components/ui/card";
import {Loader} from "@/src/modules/core/components";
import useResidentHouseholdId from "@/src/modules/core/hooks/use-resident-household-id";
import {useStoredMe} from "@/src/modules/core/store/me";
import {useStoredUserProperty} from "@/src/modules/core/store/user-property";
import useHousehold from "@/src/modules/households/hooks/use-household";
import {getHouseholdAssignedTo} from "@/src/modules/households/lib/household-assigned-to";
import {formatHouseholdPin} from "@/src/modules/households/lib/household-combobox-display";
import {formatPropertyAddressLine} from "@/src/modules/properties/lib/property-address";
import type {Property} from "@/src/modules/properties/types";
import {cn} from "@/lib/utils";

import {
  RESIDENT_HOME_CARD_CONTENT_CLASS,
  RESIDENT_HOME_CARD_SHELL_CLASS,
} from "../lib/resident-home-block-layout";
import {resolveHouseholdMapPosition} from "../lib/resident-home-map";
import {RESIDENT_HOME_PROPERTY_ICON} from "../lib/resident-home-teaser";
import {ResidentHomeIconTextBlock} from "./resident-home-icon-text-block";
import {ResidentHomeNeedHelpFooter} from "./resident-home-support-strip";
import {ResidentHomeShareAddressButton} from "./resident-home-share-address-button";
const HOUSEHOLD_MAP_CLASS = "h-40 w-full shrink-0 sm:h-36 sm:w-56 md:w-64 lg:w-72";

type ResidentHomeWelcomeProperty = Pick<
  Property,
  | "name"
  | "number"
  | "street"
  | "unit_number"
  | "city"
  | "state"
  | "zipcode"
  | "gps_coordinates"
>;

type ResidentHomeWelcomeCardProps = {
  firstName: string;
  property?: ResidentHomeWelcomeProperty | null;
  supportPhone?: string | null;
  className?: string;
};

function formatPropertySummary(property: ResidentHomeWelcomeProperty): string | null {
  const name = property.name?.trim() || null;
  const address = formatPropertyAddressLine(property);
  const summary = [name, address].filter(Boolean).join(" · ");
  return summary || null;
}

export function ResidentHomeWelcomeCard({
  firstName,
  property,
  supportPhone,
  className,
}: ResidentHomeWelcomeCardProps) {
  const me = useStoredMe();
  const userProperty = useStoredUserProperty();
  const householdId = useResidentHouseholdId();
  const {household, isPending} = useHousehold(householdId);

  const [mapPosition, setMapPosition] = useState<[number, number] | null>(null);
  const [mapPending, setMapPending] = useState(false);

  const residentName =
    getHouseholdAssignedTo(household?.assigned_to)?.name?.trim() ||
    [me?.first_name, me?.last_name].filter(Boolean).join(" ").trim() ||
    firstName.trim() ||
    "there";
  const householdAddress = household ? formatPropertyAddressLine(household) : null;
  const householdPin = household ? formatHouseholdPin(household.pin) : null;
  const propertySummary = property ? formatPropertySummary(property) : null;
  const trimmedSupportPhone = supportPhone?.trim() || "";

  useEffect(() => {
    if (!householdId || !household) {
      setMapPosition(null);
      setMapPending(false);
      return;
    }

    let cancelled = false;
    setMapPosition(null);
    setMapPending(true);

    void resolveHouseholdMapPosition(household, userProperty?.gps_coordinates).then((position) => {
      if (!cancelled) {
        setMapPosition(position);
        setMapPending(false);
      }
    });

    return () => {
      cancelled = true;
    };
  }, [householdId, household, userProperty?.gps_coordinates]);

  const showMapSlot = Boolean(household && (mapPending || mapPosition));

  const hierarchyBody = (
    <div className="flex min-w-0 flex-1 flex-col gap-0.5">
      <p className="text-foreground text-sm font-semibold">Welcome back, {residentName}</p>
      {householdId && isPending ? (
        <Loader className="min-h-8 py-1" />
      ) : householdAddress ? (
        <div className="flex min-w-0 flex-col gap-0">
          <div className="text-foreground text-pretty text-sm leading-snug">
            {householdAddress}
            <ResidentHomeShareAddressButton
              residentName={residentName}
              address={householdAddress}
              className="ml-1.5 inline-flex align-middle"
            />
          </div>
          {householdPin ? (
            <p className="text-foreground text-sm leading-snug">PIN {householdPin}</p>
          ) : null}
        </div>
      ) : householdId ? (
        <p className="text-muted-foreground text-sm">Household address unavailable.</p>
      ) : (
        <p className="text-muted-foreground text-sm">No household is linked to your account yet.</p>
      )}
      {propertySummary ? (
        <p className="text-muted-foreground text-pretty text-sm leading-snug sm:truncate">
          {propertySummary}
        </p>
      ) : null}
    </div>
  );

  return (
    <Card
      className={cn(
        RESIDENT_HOME_CARD_SHELL_CLASS,
        "border-primary/20 bg-gradient-to-br from-primary/[0.04] to-card",
        className,
      )}
    >
      <CardContent className={RESIDENT_HOME_CARD_CONTENT_CLASS}>
        <div className="flex flex-col gap-3 sm:flex-row sm:items-stretch">
          <div className="flex min-w-0 flex-1 flex-col gap-3">
            <ResidentHomeIconTextBlock
              icon={RESIDENT_HOME_PROPERTY_ICON.icon}
              iconWrapperClassName={RESIDENT_HOME_PROPERTY_ICON.iconWrapperClassName}
              iconClassName={RESIDENT_HOME_PROPERTY_ICON.iconClassName}
              className="min-w-0 flex-1 items-start"
              title={hierarchyBody}
            />
            {trimmedSupportPhone ? (
              <div className="border-border border-t pt-3">
                <ResidentHomeNeedHelpFooter supportPhone={trimmedSupportPhone} className="border-0 pt-0" />
              </div>
            ) : null}
          </div>

          {showMapSlot ? (
            mapPending ? (
              <Loader className={cn(HOUSEHOLD_MAP_CLASS, "sm:self-center")} />
            ) : mapPosition ? (
              <MapEmbed
                key={householdId}
                position={mapPosition}
                className={cn(HOUSEHOLD_MAP_CLASS, "sm:self-center")}
              />
            ) : null
          ) : null}
        </div>
      </CardContent>
    </Card>
  );
}
